import React, { useState } from "react";
import * as XLSX from "xlsx";

const DoctorSalesModal = ({ doctor, sales, onClose }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const normalizeDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toISOString().split("T")[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    const date = new Date(timeString);

    // Add 5 hours
    date.setHours(date.getHours() + 5);

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const filteredSales = sales.filter((sale) => {
    const saleDate = normalizeDate(sale.date);
    return (
      (!startDate || saleDate >= startDate) && (!endDate || saleDate <= endDate)
    );
  });

  const totalFee = filteredSales.reduce((sum, sale) => sum + sale.fee, 0);

  const exportToExcel = () => {
    const dataToExport = filteredSales.map((sale, index) => ({
      "Sr No.": index + 1,
      "Checkup Date": formatDate(sale.date),
      "Checkup Time": formatTime(sale.created_at),
      "Patient Name": sale.full_name,
      "Patient ID": sale.patient_id,
      Fee: sale.fee,
      "Hospital Share": sale.hospital_tax,
      "Doctor Share": sale.commission_fee,
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Doctor Sales");
    XLSX.writeFile(wb, `${doctor}_Sales.xlsx`);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-2 sm:p-4 md:p-6"
      // onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full 
          max-w-full sm:max-w-[90%] md:max-w-[75%] lg:max-w-[65%]
          max-h-[95vh] sm:max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="bg-[#1F2937] px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 mr-3">
            <p className="text-[10px] font-medium uppercase tracking-widest text-gray-400">
              Sales Records
            </p>
            <p className="text-white font-semibold text-sm sm:text-base leading-tight mt-0.5 truncate">
              {doctor}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full
              bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white
              transition-all duration-200 text-lg font-bold leading-none"
          >
            &times;
          </button>
        </div>

        {/* ── Avatar Strip ── */}
        <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 sm:px-5 sm:py-3 flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {doctor?.charAt(0)?.toUpperCase() || "D"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
              {doctor}
            </p>
            <p className="text-[11px] text-gray-400">
              {filteredSales.length} record(s) found
            </p>
          </div>
        </div>

        {/* ── Filter Strip ── */}
        <div className="bg-white border-b border-gray-100 px-4 py-3 sm:px-5 flex-shrink-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* From */}
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 whitespace-nowrap">
                From
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-200 bg-gray-50 text-xs text-gray-700 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 w-full sm:w-auto"
              />
            </div>

            {/* To */}
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 whitespace-nowrap">
                To
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-200 bg-gray-50 text-xs text-gray-700 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 w-full sm:w-auto"
              />
            </div>

            {/* Clear */}
            <button
              onClick={handleClearFilters}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Clear
            </button>

            {/* Download */}
            <div className="w-full sm:w-auto sm:ml-auto">
              <button
                onClick={exportToExcel}
                className="w-full sm:w-auto text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                Download Excel
              </button>
            </div>
          </div>
        </div>

        {/* ── Table Area with fixed tfoot ── */}
        <div className="flex flex-col flex-1 overflow-hidden px-3 py-3 sm:px-5 sm:py-4">
          {filteredSales.length > 0 ? (
            <div className="flex flex-col overflow-hidden border border-gray-200 rounded-xl">
              {/* Scrollable rows — fixed at ~15 rows (~40px each) */}
              <div
                className="overflow-y-auto overflow-x-auto"
                style={{ maxHeight: "600px" }}
              >
                <table className="w-full min-w-[480px] text-xs sm:text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap sticky top-0 bg-gray-50 z-10">
                        #
                      </th>
                      <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap sticky top-0 bg-gray-50 z-10">
                        Patient ID
                      </th>
                      <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap sticky top-0 bg-gray-50 z-10">
                        Date
                      </th>
                      <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap sticky top-0 bg-gray-50 z-10">
                        Time
                      </th>
                      <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap sticky top-0 bg-gray-50 z-10">
                        Patient Name
                      </th>
                      <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-right whitespace-nowrap sticky top-0 bg-gray-50 z-10">
                        Fee
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.map((sale, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {sale.patient_id}
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                          {formatDate(sale.date)}
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                          {formatTime(sale.created_at)}
                        </td>
                        <td className="px-3 py-2.5 text-gray-800 font-medium">
                          {sale.full_name}
                        </td>
                        <td className="px-3 py-2.5 text-gray-800 font-semibold text-right whitespace-nowrap">
                          PKR {Number(sale.fee).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Fixed Total Footer — always visible */}
              <div className="flex-shrink-0 border-t-2 border-gray-200 bg-gray-50">
                <table className="w-full min-w-[480px] text-xs sm:text-sm border-collapse">
                  <tfoot>
                    <tr>
                      <td
                        colSpan="5"
                        className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-gray-500"
                      >
                        Total ({filteredSales.length} records)
                      </td>
                      <td className="px-3 py-2.5 text-gray-800 font-bold text-right whitespace-nowrap">
                        PKR {Number(totalFee).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400 text-sm">
              No sales records found for the selected date range.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorSalesModal;
